/**
 * New format for ad unit initialization
 * This is used in case the ad units for a publisher needs extra configuration
 */

/**
 * @targets - a key value pair where the key is the div, the value can be empty or contain an insert strategy and a selector
 * in case the insert and selector are not provided, platform will insert the ad inside the mentioned target
 * in case they are, platform will create the vuukle-ad or other target div itself and insert it before/after/inside the selector's element
 * adUnit can also be overriden inside the targets key-value pair
 * @insert - insertion strategy for targets, should be 'inside' or 'before or 'after'
 * @selector - a css selector or a javascript code resulting in an html element
 * @hideAdLabel - The ads come with 'Advertisement' label, we sometimes don't need to show it, this exists for hiding it (if 'true')
 * @stickyAd - Makes the ad stick to the bottom
 * @customCSS - apply custom CSS on targets. Not required. It doesn't work on default and secondDefault targets (if CW and Emotes) as they receive default CSS already
 * In case any of the custom target elements are missing in the website the platform will log a warning message just like with default targets
 * @AAXID - Override the default Acceptable Ads Exchange id per publisher
 * @trafficExchangeRows - Number of article rows for traffic exchange script
 * @insertTX - config for inserting TX somewhere in the page manually
 * @insertTX @insert - insertion strategy for TX, should be 'inside' or 'before or 'after'
 * @insertTX @selector - a css selector or a javascript code resulting in an html element
 * @hideLabelLogo - Hide Vuukle logo from advertisement labels
 */

export default {
  // fake configuration to serve as an example
  'fake-key': {
    targets: {
      default: {}, // no config means the ad will be inserted inside the target and publisher must create the ad div in their site
      secondDefault: {},
      'vuukle-ad-3': {
        insert: 'before', // we also have 'inside','before' options
        selector: '.cat_img1', // a css selector
        hideAdLabel: true, // No label for this specific ad, not mentioning hideAdLabel or giving 'false' means that the ad label will be present
      },
      'vuukle-ad-4': {
        insert: 'inside', // kind of redundant but providing it helps us understand where the ad goes, not providing assumes 'inside'
        // javascript expression that returns an html element before which we will insert our target div with ad
        selector: () => document.querySelector('.cat_img1'),
      },
      'vuukle-ad-5': {
        insert: 'after',
        // a more elaborate javascript example
        selector: () => {
          // any kind of javascript code goes here but it must return an element otherwise platform won't create this ad div
          const element = document.querySelector('.cat_img');
          // if our specific ad spot exists, let's put it inside there otherwise put it in a different spot spot
          return element ? element : document.querySelectorAll('.cat_img1')[0];
        },
      },
      'vuukle-ad-6': {
        stickyAd: true, // we're having this ad at the bottom and sticky
      },
    },
    customCSS: {
      'vuukle-ad-4': 'width: 666px; height: 666px;', // devil's ADvocate
    },
    blockKeywords: ['blood', 'gore', 'destruction'], // do not create ads in articles with these words
  },
  'fake-key-2': {
    trafficExchangeRows: 4,
  },
  // mixi.media
  'cc5c4d9e-1531-4a59-98ac-4e4b018f4e84': {
    externalConfig: true,
  },
  // nbc-2
  '8f291d07-5577-46be-ae2e-b4782cc3a034': {
    externalConfig: true,
  },
  // pudhari.news
  'cd1839ec-6b95-4656-8275-1f7217624c25': {
    externalConfig: true,
  },
  // deccanchronicle
  'de0e13e8-531d-43ce-a154-0cc6451118ef': {
    externalConfig: true,
  },
  // filmyfocus.com
  '0e3c0415-8433-4bf7-9ed3-88671cc02e33': {
    externalConfig: true,
  },
  // uttranews.com
  'b7257a34-e29d-4c2c-b84d-c84db5990876': {
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // thekashmirmonitor.net
  'b1e9b4de-dc94-461c-b12b-89c7da44452b': {
    externalConfig: true,
  },
  // thepoc.ro
  '52036386-dc13-49cd-93c5-f4f9b965e3de': {
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // erelego
  '456e6e4f-b26d-43be-9bfa-6b977779fd06': {
    externalConfig: true,
  },
  // chennaimemes.in
  'af76e530-24a0-4452-9c07-71f255ce0b48': {
    externalConfig: true,
  },
  // philstar.com
  '75f932c6-b01d-490e-bae9-2a145b13e07d': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-8': {
        insert: 'after',
        selector: '#sports_article_writeup > p:first-of-type',
        adUnitGeos: ['CN'],
      },
    },
  },
  // taxguru.in
  '19408c72-00ea-4b57-9404-11a5c402347d': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-16': {},
      'vuukle-ad-3': {},
    },
  },
  // opsco
  '03538d2f-71cb-43bd-ae9d-2a54e79fa485': {
    externalConfig: true,
    compactQuizzly: true,
  },
  // gulfnews
  'bc0e08e5-de64-467f-9734-9f91d08bf156': {
    ads: false,
    compactQuizzly: true,
  },
  // tribuneindia.com
  '02bf6939-9a29-48d7-bb91-7ed2e2253b49': {
    externalConfig: true,
  },
  // skyads.vn
  'efc2a3e8-20a2-4926-98fe-7cda901acd6d': {
    externalConfig: true,
  },
  // newdelhiairport.in
  '3a1799f0-d1e4-4d9c-a9bc-85ef64ca99bb': {
    externalConfig: true,
  },
  // foxpush.com
  '62fe2bc2-4db6-4d31-a4b5-5d498ccbe80d': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // asianhobbyist.com
  '3452da96-daaa-42ff-9bc8-3c135dbc7d23': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // capitalmarket.com
  '70691e3b-1c6c-491c-8209-b813aeed2f57': {
    externalConfig: true,
  },
  // vijayavani.net
  '4c84223a-94bf-47cd-9aa8-ba5e91874df4': {
    externalConfig: true,
  },
  // nagalandpost.com
  'c910cb42-e15e-4ea5-b571-cc8ce018681d': {
    externalConfig: true,
  },
  // deepika.com
  'ceccf8f6-9d1c-41e6-9a95-3addc14a3d27': {
    externalConfig: true,
  },
  // madhyamam.com
  '8dcad33b-ae75-4338-97c8-787e48248fe1': {
    externalConfig: true,
  },
  // nwnews24.com
  '6278c65e-86ab-454e-b8a7-611da3adba57': {
    externalConfig: true,
  },
  // epaper.heraldgoa.in
  'be7b324d-fb59-411f-a504-dc616ce96b5e': {
    externalConfig: true,
  },
  // gizchina.com
  '3a4037f9-61aa-4d84-a31b-0ec8aa0f0f3a': {
    externalConfig: true,
  },
  // gr.gizchina.com
  'e5baaad6-2cbb-4bf5-9087-86a3bda4d2ab': {
    externalConfig: true,
  },
  // explorenetworth.com, ethnicitywiki.com
  '4a03593d-3c83-4e2f-b391-2d9ceeb2f1ac': {
    externalConfig: true,
  },
  // daijiworld.com
  '5f9ec46f-8406-4c61-97cc-205f813fdf66': {
    externalConfig: true,
  },
  // patriotproject.com
  'fc82625a-e20a-4130-88a5-d07aaa5359da': {
    targets: { default: {} },
  },
  // mamasuncut.com
  '33c2d442-2a4a-4249-b721-badaa60a1da4': {
    targets: { default: {} },
  },
  // themeowblogwebmaster
  'e139add8-761d-4f99-b843-fb69a2db83bf': {
    targets: { default: {} },
  },
  // manoramaonline.com
  '0f8151a9-0f88-4732-8b7e-b5b0e4b1417e': {
    externalConfig: true,
  },
  // joblo.com
  'ce56389c-23a7-4874-a8ae-971a18a84355': {
    externalConfig: true,
  },
  // Khaleejtimes.com
  '841fb3e5-977f-4e2e-be39-fae608323cc5': {
    externalConfig: true,
  },
  // thebrighterside
  'd59ac8ae-352a-45d3-bd28-087702e2334c': {
    externalConfig: true,
  },
  // radiofacts.com
  'd4ff715a-b8ff-4dd9-86d1-d794cc51a865': {
    externalConfig: true,
  },
  // issuu.com
  '61f6b959-351b-4a5f-8371-7bfbcb7f33f2': {
    targets: { default: {}, 'vuukle-ad-3': { hideAdLabel: true } },
  },
  // sakshi.com
  'e0ec1b7e-b701-4297-b283-30cba62bc13d': {
    externalConfig: true,
  },
  // thesaurus.net
  'd234188b-87e4-4033-98c3-c3f86d007de2': {
    externalConfig: true,
  },
  // bqprime.com
  '43be4c85-8242-45c6-84d2-7ec426d8fd34': {
    targets: {
      default: {},
      'vuukle-ad-3': {
        insert: 'inside',
        selector: '#adSlot-0',
        hideAdLabel: true,
      },
      'vuukle-ad-4': {},
    },
  },
  // blitz
  'b919eaf5-d0c8-4b27-a692-58b6e26f9f1b': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
      'vuukle-ad-6': {},
    },
  },
  // washingtonexaminer.com
  '716a8af7-beeb-4fe5-9d57-8c467fb83eb4': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
    },
  },
  // letsrun.com
  '8eafc07f-8a53-4dfa-8185-8be6b109d682': {
    externalConfig: true,
  },
  // littlethings.com
  '78f75f1e-1377-4716-9559-5915ec1c570b': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
    },
  },
  // vikatan.com
  '723182a6-c9d2-4dad-83c7-b83b272390df': {
    targets: { default: {} },
    externalConfig: true,
  },
  // r-lang.com
  '2a65e0fb-d42a-494e-b1a9-8c518b7e4a51': {
    externalConfig: true,
  },
  // thebuffalofanatics.com
  'e34028fa-a74d-4840-a3ad-1571088e224d': {
    externalConfig: true,
  },
  // explainamerica.com
  '7f7c8cb3-6bba-4f41-9c6b-4eba71594c34': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
      'vuukle-ad-6': {},
      'vuukle-ad-10': {},
      'vuukle-ad-11': {},
    },
  },
  // 10tv.in
  '1da04a0e-e770-4351-a6d3-8d072a7de4d4': {
    targets: {
      default: {},
    },
  },
  // ndtv.com
  'e6741053-104a-43d2-8bd8-b2536529f5cd': {
    externalConfig: true,
  },
  // greatandhra.com
  '2b166297-6273-48a9-82e9-696327c67418': {
    externalConfig: true,
  },
  // entertainment-focus.com
  '611b4adf-3a1b-428b-9196-b654480065d0': {
    externalConfig: true,
  },
  // greenwichmeantime.com
  '9beb77ad-d1e5-4a81-b987-bfa00b7e7c39': {
    externalConfig: true,
  },
  // zeenews.india.com
  '1aef4996-c67f-4857-8ec7-fc037d518060': {
    fpd: true,
    network_code: '11440465',
    externalConfig: true,
  },
  // news-fair.com
  '649aa9ef-0293-4691-adc5-7499bc5c6ff9': {
    externalConfig: true,
  },
  // inquirer.net
  'd45dc90a-b6cc-47b1-915e-9db149d05ace': {
    gaEvents: true,
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // agrifarming.in
  'c413f561-01f9-44c3-b2c9-a148d93e35b5': {
    externalConfig: true,
  },
  // fark.com
  '4d5b75f5-e653-4e7a-b60f-f8002d55864a': {
    externalConfig: true,
  },
  // voiceofmotown.com
  '7d4b0bc3-14e0-4fe9-b59d-5290fd3d1765': {
    externalConfig: true,
  },
  // 24x7tamil.com
  '8b20ec9c-af8e-432e-bc29-630959e0909e': {
    targets: { default: {}, secondDefault: {} },
  },
  // staraudiobooks.net
  '316edba4-941d-45a1-8bbd-c39f74de12b2': {
    targets: { default: {}, secondDefault: {} },
  },
  // khaboronline.com
  '7c2d8865-b295-40ff-9fdc-3e6280c0708c': {
    targets: { default: {}, secondDefault: {} },
  },
  // wzmq19.com
  '362229ba-8929-47f4-b350-4bf068441b90': {
    targets: { default: {}, secondDefault: {} },
  },
  // mathrubhumi.com
  'cd78325e-2fce-440c-b899-83d68d5deb38': {
    externalConfig: true,
  },
  // awarenessact.com
  '9bc3118f-c2c8-4983-aed1-82592d0a4acf': {
    externalConfig: true,
  },
  // emeatribune.com
  'ed66dfc0-c87c-43e3-a621-792c5398d0b9': {
    targets: { default: {}, secondDefault: {} },
  },
  // fishgame.com
  '3ca1e321-a3ba-4c1b-9e8f-9d08f31cf98e': {
    targets: { default: {}, secondDefault: {} },
  },
  // healthcarechannel.co
  'aeff3bf7-955f-44bd-a866-58374584976c': {
    targets: { default: {}, secondDefault: {} },
  },
  // irishcentral.com
  '38d72883-04af-45d6-a1a6-3bb8ca506bd9': {
    gaEvents: true,
    externalConfig: true,
    targets: { default: {}, secondDefault: {} },
  },
  // galatta.com
  '9291c2db-d0e1-49a1-8624-7da72ed36872': {
    targets: { default: {}, secondDefault: {} },
  },
  // 10to5.in
  '592ca9ac-7f01-43f3-ba82-2ff7b93a7650': {
    externalConfig: true,
  },
  // newslinks.news
  '53ebd8c7-b7cf-42c1-9640-a55165bca9ab': {
    targets: { default: {}, secondDefault: {} },
  },
  // mirchi9.com
  '248b9715-3d95-42f8-9cc4-7b32abb3c715': {
    externalConfig: true,
  },
  // dharashivlive.com
  '848e7004-41d9-4084-934f-26f3dedec5a1': {
    targets: { default: {}, secondDefault: {} },
  },
  // politicalflare.com
  '0b420d00-5895-421c-9dab-326f29b1243e': {
    targets: { default: {}, secondDefault: {} },
    insertQuizAndAd: {
      quizOptions: {
        insert: 'prependInside',
        selector: '#vuukle-comments',
        postQuizInsertionCallback: () => {
          const ad = document.querySelector<HTMLElement>('#vuukle-comments > .vuukle-ads');
          const insertAdNearQuiz = (container: HTMLElement, ad: HTMLElement) => {
            if (container && ad) {
              container.appendChild(ad);
              const style = document.createElement('style');
              style.innerHTML = `
                #vuukle-quiz-container {
                  gap: 15px;
                }
                #vuukle-quiz-container .vuukle-ads {
                  margin: 0 !important;
                  min-height: 250px !important;
                }
              `;
              document.head.appendChild(style);
            }
          };
          const runIntervalForQuizAndAd = () => {
            const interval = setInterval(() => {
              const theAd = document.querySelector<HTMLElement>('#vuukle-comments > .vuukle-ads');
              const quizCont = document.querySelector<HTMLElement>('#vuukle-quiz-container');
              if (quizCont && theAd) {
                clearInterval(interval);
                insertAdNearQuiz(quizCont, theAd);
              }
            }, 100);
          };
          if (ad) {
            const quizContainer = document.querySelector<HTMLElement>('#vuukle-quiz-container');
            if (quizContainer) {
              insertAdNearQuiz(quizContainer, ad);
            } else {
              runIntervalForQuizAndAd();
            }
          } else {
            runIntervalForQuizAndAd();
          }
        },
      },
    },
  },
  // 123greetings.com
  '11ea27b6-8f82-4fed-95cc-77748a7454a2': {
    targets: { 'vuukle-ad-3': {} },
  },
  // telanganatoday.com
  'b020f681-0903-4e67-8436-b0208a3b3423': {
    externalConfig: true,
  },
  // ProCoinNews.com
  '15a53ea9-6592-410d-8233-d60dbadf2f48': {
    externalConfig: true,
  },
  // gladwire.com
  '92fd8992-dbb2-4d42-9cc3-1b8101c44b9b': {
    externalConfig: true,
  },
  // walterfootball.com
  '977dd828-0657-4af3-8db8-eb6d10564bde': {
    targets: { default: {}, secondDefault: {} },
    initAfterScroll: true,
    initAfterScrollPixels: 100,
  },
  // hawtcelebs.com
  'f4063dfe-ef7d-4812-9809-ec5a33a2fb85': {
    targets: { default: {}, secondDefault: {} },
  },
  // salahuddinayyubi.com
  'a7cb6247-6a8a-4519-8945-a88e6f5377b0': {
    targets: { default: {}, secondDefault: {} },
  },
  // philstarlife.com
  'f985df63-157a-4bf3-b09d-13eec2eda561': {
    externalConfig: true,
  },
  // timesofsandiego.com
  '52df42fa-e94e-457b-b746-e743318a0458': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // codeslide
  '7d722fa3-04d6-4455-a1d5-f322533f8f98': {
    externalConfig: true,
  },
  // mediahiburan.my
  '3779bc94-f3ce-4138-af5d-1d3c0d7ee3b8': {
    targets: { default: {} },
  },
  // gript.ie
  'ff5b00dd-18d5-409a-b1b7-9742f68f5364': {
    targets: { default: {}, secondDefault: {} },
  },
  // schooltube.com
  '356fca96-24f8-4af6-b2f3-5abb75add2a9': {
    gaEvents: true,
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // moguldom.com
  '767c9051-df7f-43a5-9ab7-5a1b6579e0b0': {
    gaEvents: true,
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // motorsport.com
  'b4a46e69-af6b-4d82-a260-8293a620a49a': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // republicworld.com
  '238f73b9-91b9-4820-9fe1-3bf995801097': {
    externalConfig: true,
  },
  // detik.com
  '53e17bd8-94eb-4a23-813e-d5f6bff1192f': {
    externalConfig: true,
  },
  // telugucinema.com
  'd8444d46-cc2d-4a45-a6ac-201f05acbfed': {
    externalConfig: true,
  },
  // Tpmedia
  '36c8c636-9cd6-4e0c-ab24-a72382ef1be1': {
    externalConfig: true,
  },
  // dasfootball.com
  'a66ef5ff-efff-4755-ab59-47667b5c4339': {
    targets: { default: {}, secondDefault: {} },
    externalConfig: true,
  },
  // hindi.webdunia.com
  '4f1ee917-bdec-49f9-a891-def444a3f541': {
    externalConfig: true,
  },
  // deccanherald
  'fb611ff0-d7a6-4ac6-933c-186a44c9132e': {
    gaEvents: true,
    showCWAudioButton: false,
    targets: { default: {} },
  },
  // asianetnews.com
  'b04a1a6f-299e-4baf-8885-cc7b956dba33': {
    externalConfig: true,
  },
  // thoughtnova.com
  'cd196cb9-3256-4804-bab8-6f2b2b210b37': {
    externalConfig: true,
  },
  // andhrajyothy.com, chitrajyothy.com
  '731b1598-53c4-43aa-9a0d-66575e146199': {
    gaEvents: true,
    externalConfig: true,
  },
  // pythonforbeginners.com
  'ddf243f4-4e19-4e9b-b734-7163f0495090': {
    hideLabelLogo: true,
    targets: {
      default: {},
      'vuukle-ad-2': {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
      'vuukle-ad-6': {},
    },
    customCSS: {
      'vuukle-ad-5': 'width: 300px',
    },
  },
  // suaramerdeka.com.my
  '34b3a8be-f0a2-40b6-8891-1d906116ba6c': {
    targets: { default: {} },
  },
  // tribune.net.ph
  '15c2fc73-2ba2-48a1-81eb-68a8d4fc7365': {
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // albeu.com
  'cf043a37-8348-4746-b692-b00faadc18c1': {
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // newskranti.com
  '87881f98-a3df-4b2e-b086-6b549524af2b': {
    targets: { default: {}, secondDefault: {} },
  },
  // tupaki
  'bc3e080c-a5bc-4730-b471-3970b6bf699e': {
    externalConfig: true,
  },
  // amarujala.com
  'efd7f6e2-802d-423d-ae84-d866ad971399': {
    gaEvents: true,
    showCWAudioButton: false,
    targets: { default: {} },
  },
  // oneindia
  '32ea1377-42f8-49bd-a80b-5a3bf4466257': {
    targets: { default: {} },
  },
  // dailyexpress.com.my
  '3da19a3c-faa3-419e-9361-4d5b8350b6f5': {
    targets: { default: {} },
  },
  // hiphoplately
  'e1023aeb-5ee1-46f2-8205-7e7372ad324f': {
    targets: { default: {} },
    externalConfig: true,
  },
  // Setn.com
  '6bfb749f-f90d-4301-89d4-0ac032d944a3': {
    targets: { default: {} },
  },
  // gulte.com
  '9433d539-1c98-49f2-ac2b-9a162d5a3d6d': {
    externalConfig: true,
  },
  // theepochtimes.com
  'c9013a0f-de25-4994-9b4b-703a6b020629': {
    externalConfig: true,
  },
  // blackenterprise.com
  'd491c427-c52e-4093-b42e-54fc7aaf72a4': {
    externalConfig: true,
  },
  // duniya
  '3632f66d-4175-4360-bc2a-cd69ad1a1d61': {
    targets: {
      default: {},
      'vuukle-ad-2': {},
      'vuukle-ad-3': {},
    },
    externalConfig: true,
  },
  // dotkeypress.kr
  'e864b6b6-579e-4c59-a4d9-949e0d25e61f': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
    },
  },
  // wikiwiki.in
  '0413d38b-e4ed-11e4-a338-002590f371ee': {
    targets: {
      default: {},
      'vuukle-ad-3': {},
    },
  },
  // cricktracker.com
  'a92e03ed-307c-4beb-b4ae-6d76b84c6466': {
    externalConfig: true,
  },
  // myjoyonline.com
  '255be353-1ccf-4eea-af0c-86d618375550': {
    externalConfig: true,
  },
  // weregeek.com
  '2a2f08e9-ff27-4020-a57b-275912fe9273': {
    targets: { default: {} },
  },
  // darklegacycomics.com
  'aade2e0e-f2fa-45c1-b262-c4b8beb5d1cb': {
    targets: { default: {} },
  },
  // lfg.co
  '64a67808-64b9-4e1c-96d8-a396bbd31caf': {
    targets: {
      default: {
        postInsertionOperation: () => {
          window.changeVuukleWidgetsTheme('dark');
        },
      },
    },
  },
  // screengeek.net
  '29ac411e-3218-42fb-90ae-47ae55444e16': {
    targets: { default: {} },
  },
  // journal.com.ph
  '2a12e2cd-bd1b-4877-8b9f-48678cda7af6': {
    targets: { default: {} },
  },
  // Thehindu.com
  '432081eb-6e8e-4abc-a88f-ca5357aa7244': {
    targets: { default: {} },
  },
  // wimp.com
  '3a0e7e26-f50d-4c57-88c2-274908569b0d': {
    showEmotesArticlesByDefault: true,
    externalConfig: true,
  },
  // Alternatifim.com
  '6a77571a-6d92-4b17-a748-5010048b4ef5': {
    targets: { default: {} },
  },
  // newsspad.com
  '91621fb2-9f22-4666-9605-614a805ef456': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // loopjamaica.com
  'f9036f75-b88a-4f5c-ae94-c9c8799a4677': {
    externalConfig: true,
  },
  // tiebreakertimes.com.ph
  '5e23e4f7-8c8f-4089-a01d-710bc798da90': {
    externalConfig: true,
  },
  // celebrityborn.com
  '0c2fffb6-b8c3-4fb0-9073-45df1a9d04ed': {
    targets: {
      default: {},
      'vuukle-ad-2': {},
    },
  },
  // Swing
  '6a39c124-67fa-4f04-8fe7-a575ecd2027c': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
      'vuukle-ad-6': {},
      'vuukle-ad-7': {},
      'vuukle-ad-8': {},
      'vuukle-ad-9': {},
    },
  },
  // sumantv.com
  'a1ab5e4d-fc4a-417a-8454-ea57c8bfaaf0': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // animemotivation.com
  'f3249e3c-6c77-4bb6-b805-d09198b21a05': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // talkbasket.net
  '12eef24a-ee2a-4ca4-8c01-64c8aa78fccd': {
    targets: { default: {}, secondDefault: {} },
  },
  // sportsmole.co.uk
  '5d6d4f55-ff35-4ac0-baaf-b5dc3ee32486': {
    targets: {
      default: {},
      'vuukle-ad-2': {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {
        insert: 'after',
        selector: '#article_body > p:first-of-type',
        adUnitGeos: ['CN'],
      },
      'vuukle-ad-5': {},
    },
  },
  // krishijagran.com
  'e1e87e0a-d5f2-4943-860d-fef621987289': {
    externalConfig: true,
  },
  // newsfront.co
  'ae417a03-65b0-45cb-9ebd-9f8ccce96b51': {
    targets: { default: {} },
  },
  // moviequotesandmore.com
  '9b305fac-eacd-470a-8cc3-d4caf68480f4': {
    externalConfig: true,
  },
  // dailyfaceoff.com
  'b1aaa3ee-e3e9-444c-b043-ad06699c7e2a': {
    targets: { default: {} },
  },
  // spurstalk.com
  '6d18d31b-113b-4676-923f-41dadfa912b0': {
    targets: { default: {} },
    externalConfig: true,
  },
  // theaviationist.com
  'f623b291-820b-4ded-a360-ef133909646c': {
    externalConfig: true,
  },
  // manilatimes.net
  'ddc3d585-821a-474d-b7da-9360d9e4f2e7': {
    externalConfig: true,
  },
  // pep.ph
  '28a76eea-83ab-4eb0-bdc9-ec32a67a8bb2': {
    externalConfig: true,
  },
  // thephoblographer.com
  'f85f9333-42c4-42c5-80b4-a9a1e3d905a1': {
    externalConfig: true,
  },
  // adops.ibtimes.com
  '7cecf616-b9b1-4fc2-9f8e-7d16ef1f7494': {
    showCWAudioButton: false,
    targets: { default: {}, secondDefault: {} },
  },
  // techtimes.com
  '6bd54cd3-71bd-475a-b1fd-25ba5a6cdcd2': {
    showCWAudioButton: false,
    targets: { default: {}, secondDefault: {} },
  },
  // euronews.com
  '1feb6d34-f2ca-4219-bff0-28be03a6d8da': {
    externalConfig: true,
  },
  // eurweb.com
  '64136411-69b5-4424-9e24-f6821f0f060e': {
    insertQuizAndAd: {
      quizOptions: {
        insert: 'after',
        selector: '.elementor-widget-theme-post-content > div > p:last-of-type',
      },
    },
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {
        insert: 'after',
        selector: '#vuukle-quiz',
      },
      'vuukle-ad-4': {
        replaceAdOnPage: {
          adDivId: 'vuukle-ad-3',
        },
      },
    },
  },
  // doyouremember.com
  '45e74a80-4a92-4e89-8a8a-4b4449edb14d': {
    externalConfig: true,
  },
  // guidepokemon.com
  '4a47762d-888d-4589-a7a3-5c06f4587bf8': {
    targets: {
      default: {},
      'vuukle-ad-2': {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
      'vuukle-ad-6': {},
    },
  },
  // guidetonote.com
  '95a1784e-cc5c-455b-a8d6-fa6170771686': {
    externalConfig: true,
  },
  // teluguone.com
  'bc9ef239-b220-46fa-9adb-9a0157bfe4ad': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // poderaopovo.com
  'eb3aedc9-183a-4364-a940-c37f4724dd2e': {
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
    },
  },
  // saamana.com, epaper.saamana.com
  'b355463c-4831-4067-8c93-a2303c39a309': {
    externalConfig: true,
  },
  //epapervijayavani.in
  'c003c14d-297a-43c9-97b2-630e05b1d9fb': {
    externalConfig: true,
  },
  //pudhari.co.in
  'e8a206d5-67d4-44bc-97af-a160da26b683': {
    externalConfig: true,
  },
  //chandrikadaily.com
  'f9488e7c-9ed2-4e12-ae31-e48f1006d071': {
    externalConfig: true,
  },
  //epaper.deshonnati.com
  '61ba9d28-1cfd-405b-87ca-3feb7326bbd6': {
    externalConfig: true,
  },
  //epaper.karavaliale.net
  'b6b7829f-9f0f-4af3-a36c-1cecf00f8baf': {
    externalConfig: true,
  },
  //punyanagari.in
  'c77634b5-93d5-413c-90ce-286bc462c5d6': {
    externalConfig: true,
  },
  // sis2sis.com
  '72a2c4c9-2fa6-47ba-b3cc-b1438b302ddc': {
    targets: { default: {}, secondDefault: {} },
  },
  // conservativehome.com
  'bc095434-27f9-4779-a350-6e9caca7f21e': {
    targets: { default: {} },
  },
  // predictinteractive.com
  '4529953f-a9cc-483a-8451-a9b0bf17a1ae': {
    hideLabelLogo: true,
    targets: {
      default: {},
      secondDefault: {},
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
      'vuukle-ad-5': {},
    },
  },
  // staging2.michaell231.sg-host.com
  'f9c98646-e2a2-4e40-92e9-58aea917d809': {
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // news.vuukle.com
  'ead41e46-a5fd-11e2-bc97-bc764e0492cc': {
    externalConfig: true,
  },
  // sonora.id
  'bf94f923-206b-4e74-9b54-8d667098c757': {
    targets: { default: {} },
  },
  // sofrep.com
  '5cda731e-bcb7-413f-984d-b76aadaea139': {
    externalConfig: true,
  },
  // thesource.com
  'a2df33e0-e37c-45d2-bf13-3176e7711b3b': {
    targets: { default: {}, secondDefault: {} },
    externalConfig: true,
  },
  // soapspoiler.com
  '83a32972-5af0-484d-ab84-c5c8c7e86d9d': {
    externalConfig: true,
  },
  // electronics-notes.com
  'f1b26352-3ac0-4373-98d3-b5d88d39602b': {
    targets: { 'vuukle-ad-3': {} },
  },
  // argbgaming.com
  'e1c9ead0-29bb-4aee-b3a9-68174fd455f8': {
    targets: { default: {}, secondDefault: {} },
  },
  // QA test host https://rosalind-07102020.netlify.app/
  '7f46a5fa-c1f6-473b-adfa-f4c9999cb7bf': {},
  // Koha
  '5c57e36e-8a31-4068-bc1b-26cf3fbf24ed': {
    showCWAudioButton: false,
    targets: {
      default: {},
      secondDefault: {},
    },
  },
  // jsnewstimes.com
  '053a2dad-4d97-4846-abdf-3ffa0a7345b2': {
    externalConfig: true,
  },
  // allaccess.com
  'e09d1311-dcda-49b0-b37b-73ba36c08d54': {
    gaEvents: true,
    targets: {
      default: {
        sideAds: 1,
      },
      secondDefault: {
        sideAds: 1,
      },
      'vuukle-ad-3': {},
      'vuukle-ad-4': {},
    },
  },
  // easygpacalculator.com
  '6f21915c-70db-4db2-b86b-463efc2927dc': {
    externalConfig: true,
  },
  // timesofindia
  '4957a26d-f83c-4d19-8538-a090303623e9': {
    externalConfig: true,
  },
  // bullscore.com
  '58625c59-58a8-44ec-9a46-72c2d7b40c6a': {
    externalConfig: true,
  },
  // stylecraze.com
  'b14f5e1c-a7f4-4957-bddb-dda8b99c151f': {
    externalConfig: true,
  },
  // tracktollywood.com
  '5f7de88a-8422-45f8-8434-33c27c88e494': {
    showCWAudioButton: false,
    targets: {
      default: {},
      secondDefault: {},
    },
    insertQuizAndAd: {
      quizOptions: {
        insert: 'prependInside',
        selector: '#vuukle-comments',
        postQuizInsertionCallback: () => {
          const ad = document.querySelector('#vuukle-comments > .vuukle-ads');
          if (ad) {
            const quizContainer = document.querySelector('#vuukle-quiz-container');
            if (quizContainer) {
              quizContainer.appendChild(ad);
            }
          }
        },
      },
    },
  },
  // pakkatelugu.com
  '78a282d5-4b03-4a1f-8a9f-4aecfa3876a3': {
    externalConfig: true,
  },
  // audizine.com
  '2cd2a90e-d838-4419-bc3a-e27006a8cdaa': {
    targets: { 'vuukle-ad-3': {} },
  },
  // toyota-4runner.org
  '6d0ad858-38ef-42d2-bc15-b5394e8ddcc3': {
    targets: { 'vuukle-ad-3': {} },
  },
  // csnbbs.com
  'dd210d92-3646-4bec-b97d-aded2022a9ec': {
    targets: { 'vuukle-ad-3': {} },
  },
  // newschoolers.com
  'd7cb4240-f167-463a-87ff-c01eda12a0a9': {
    targets: { 'vuukle-ad-3': {} },
  },
  // cheatermad.com
  'd19bdefa-b13c-47cd-84e2-e161261bace5': {
    targets: { 'vuukle-ad-3': {} },
  },
  // alligator.org
  '025dc823-7837-4535-8909-79f78a0e64f1': {
    targets: { 'vuukle-ad-3': {} },
  },
  // rogerebert.com
  '04c7aee7-224b-440d-ac9b-cb199abf2358': {
    targets: { 'vuukle-ad-3': { hideAdLabel: true } },
  },
  // carolinahuddle.com
  '19049fe7-a743-4212-b91f-345332984a00': {
    targets: { 'vuukle-ad-3': {} },
  },
  // zegtrends.com
  'b9bc1dcc-e97d-4033-82b4-96b63072c472': {
    targets: { 'vuukle-ad-3': {} },
  },
  // unknowncheats.me
  '31520fb1-51f3-4521-af23-3ce5730f4a28': {
    targets: { 'vuukle-ad-3': {} },
  },
  // desiretoinspire.net
  '656d28e4-0a86-440b-ba38-a154c0eac0f7': {
    targets: { 'vuukle-ad-3': {} },
  },
  // beachgrit.com
  '76d59655-9d32-4bf3-8bba-ea625ee044a0': {
    targets: { 'vuukle-ad-3': {} },
  },
  // audiokarma.org
  'bb68512c-1daf-4aae-95c2-943675f03da2': {
    targets: { 'vuukle-ad-3': {} },
  },
  // untappd.com
  'ce57bead-42f7-45b9-a6ee-7d95dcb6f53e': {
    targets: { 'vuukle-ad-3': {} },
  },
  // kids-in-mind.com
  '54f5f0ae-60c4-467a-ac88-715728dfb0e5': {
    externalConfig: true,
  },
  // beeradvocate.com
  '9a65254d-e0d8-40ce-a209-8513ae24cbab': {
    targets: {
      'vuukle-ad-3': {
        postInsertionOperation: () => {
          const adElement = document.querySelector('#vuukle-ad-3');
          if (adElement && adElement.parentElement) {
            adElement.parentElement.style.float = 'right';
          }
        },
      },
    },
  },
  // CastingCall.Club
  '76135452-44ce-41b4-a49d-fc6f19545533': {
    targets: { 'vuukle-ad-3': {} },
  },
  // airportinfo.live
  'f7f415c2-121f-4782-a7f7-8b3e41ac8388': {
    targets: { 'vuukle-ad-3': {} },
  },
  // thebeat99.com
  '505c1185-b850-4243-830e-966153f5dc66': {
    externalConfig: true,
  },
  // thebeat99ph.com
  '8c4da6b7-780a-4ade-86d1-887c30e5af8b': {
    externalConfig: true,
  },
  // thebeat97.com
  '6494c164-044d-4311-9d91-53947348c007': {
    externalConfig: true,
  },
  // thebeat97.fm
  'eaa782ec-5d98-4cbf-b2e4-6ecb1fa630f3': {
    externalConfig: true,
  },
  // thebeat1036.com
  '869226d0-a742-450a-94a2-17e7a43659f7': {
    externalConfig: true,
  },
  // classic94.fm
  '2bc125b8-ae0d-4a68-b6de-d6b324e35934': {
    externalConfig: true,
  },
  // classic91.net
  'de8d5bd5-1ea0-44d4-a0d4-c519299d80c1': {
    externalConfig: true,
  },
  // classic97.net
  '227dda61-ad39-440b-9ffd-4f1dbf1644b0': {
    externalConfig: true,
  },
  // naija927.com
  'c79b75ad-35ab-440b-b099-f4574ba39771': {
    externalConfig: true,
  },
  // naija102.com
  '2ef1400c-04eb-4838-8ffa-28232fe5a214': {
    externalConfig: true,
  },
  // lagostalks.com
  '81ac9e4a-bbdd-456f-a9da-02819977dc9b': {
    externalConfig: true,
  },
  // firstsportz.com
  'c1cb99b8-515c-4bf4-8f98-5ceb2be6811b': {
    externalConfig: true,
  },
  // whatanikasays.com
  '8e770cea-4778-40ff-b06c-c51d851e2ca0': {
    externalConfig: true,
  },
  // trendsandtactics.com
  '2abb8a65-52a3-4813-8d9e-60fc98eadeb6': {
    externalConfig: true,
  },
  // thethriftyapartment.com
  '56f2bd7a-86e1-4018-be4c-386831006619': {
    externalConfig: true,
  },
  // neonmoon.co
  'ceb3d467-a603-4ca6-b10d-36bb629f916c': {
    externalConfig: true,
  },
  // topdollarinvestor.com
  'b4318906-5f52-4d6c-b40b-dd6a2e6f97fb': {
    externalConfig: true,
  },
  // manandhome.com
  '24620f34-3043-44b6-a4e6-4cd1efd120c9': {
    externalConfig: true,
  },
  // kindafrugal.com
  '327a8f65-b02f-4aaa-b6ad-b7aafbc1331d': {
    externalConfig: true,
  },
  // healthmeanswealth.com
  'd2e04f5f-fe35-494e-a528-a99a86305307': {
    externalConfig: true,
  },
  // financiallywelloff.com
  '953f7799-6f90-450d-a8fa-c5eafad5c1c3': {
    externalConfig: true,
  },
  // ecolodgesanywhere.com
  '6b12d066-5401-49d6-9b0d-075088382dde': {
    externalConfig: true,
  },
  // centsandpurpose.com
  'b7e47a2e-01e6-49ef-b066-df66ae891595': {
    externalConfig: true,
  },
  // buzzloving.com
  'ba36348e-4b61-4e5d-928c-5669c074ab21': {
    externalConfig: true,
  },
  // bookwormera.com
  '4b2136ed-d349-4f19-9d57-246dff67bb7f0': {
    externalConfig: true,
  },
  // bluecollarbrain.com
  '407ac1f4-1c19-4493-bdef-f7e9236b1515': {
    externalConfig: true,
  },
  // listal.com
  '42164773-827e-42bd-8c5c-91bb25840611': {
    targets: {
      'vuukle-ad-3': {},
    },
  },
  // dailyvoice.com
  '2fd387b9-f020-4c23-96ee-bd7b92af6099': {
    externalConfig: true,
  },
  // nation.africa
  '36c1b54c-8fbb-4400-8920-4a3210ccc7c5': {
    externalConfig: true,
  },
  // outlookindia.com
  'cc5f8583-5b87-4aac-9f59-f99c24c827c0': {
    externalConfig: true,
  },
  // thegeorgiagazette.com
  '2920c103-3b04-4f5b-9f5f-15163159408b': {
    externalConfig: true,
  },
  // brit.co
  '84b63afa-2046-4d08-883a-073f6f516695': {
    externalConfig: true,
  },
  // ghanagossips.com
  '15801e19-3883-480d-8fb9-c27d6f8f6dc5': {
    externalConfig: true,
  },
  // flyfmghana.com
  '5069a9fd-ea6a-4209-8356-e79bbb2c7e7f': {
    externalConfig: true,
  },
  // queerty.com
  '24ab14c8-776c-4198-9bc1-ef52c930649b': {
    ads: false,
  },
  // football365.com
  'bc9fd4eb-d7be-4d00-87ef-b570563b015f': {
    externalConfig: true,
  },
  // test host https://relaxed-beaver-76304e.netlify.app/ belongs to sometempmail@yopmail.com
  '664e0b85-5b2c-4881-ba64-3aa9f992d01c': {
    externalConfig: true,
  },
  // test host https://inspiring-northcutt-dbfff7.netlify.app/
  '54c90d10-586c-4036-9ffd-bc54eaf71af1': {
    compact: true,
  },
  // Used for bunch of test hosts like https://technical-city-demo.netlify.app/
  '28c15853-07ca-4cae-9ba9-e2b08ede81c4': {
    externalConfig: true,
  },
  // heavy.com
  '6ff6a1d2-d34b-462f-bf11-c86cae0e6821': {
    externalConfig: true,
  },
  // fandom.com
  'cbd86e60-4c2d-46ed-b4fe-5c486ef6bdec': {
    targets: {
      'vuukle-ad-3': { hideAdLabel: true },
    },
  },
  // beta.tv9hindi.com
  '691b9feb-5362-4858-bbdd-e4eb539cbd23': {
    externalConfig: true,
  },
  // indianexpress.com
  '2e5a47ef-15f6-4eec-a685-65a6d0ed00d0': {
    externalConfig: true,
    hideAddVuukle: true,
    showQuizAnswerRightOrWrongOnLogin: true,
  },
  // bossip.com
  'f1533684-e153-4225-becd-6017d3b37fcf': {
    CWBubblePersistence: true,
    externalConfig: true,
    excludeFromEmptyEmotesQuizInsertion: true,
  },
} as { [key: string]: any };
